import { Fragment } from 'react';
import { dayText } from '../../audit/findings';
import { PersonName } from '../PersonName';

/**
 * A task-history line as people read it (UI review U190/U191, source pass
 * tasks.ts): in the app's OWN lines only — a due-date change's days
 * ("2026-11-02" → "Nov 2, 2026") and a reassignment's addresses (→ the
 * person, via PersonName). Every other kind (files, completions, reopen
 * reasons) carries people's own words and is shown exactly as written —
 * a file named "Screenshot 2026-10-19…" stays as is, and an address
 * someone typed never turns into a name (QA + security 2026-10-28).
 * Display only: the stored line is unchanged, so older entries read the
 * same way.
 */
const FORMATTED_KINDS = new Set(['due_date', 'reassigned']);
/** The app writes these lines; anything longer isn't one of them. */
const MAX_BODY = 500;
const TOKEN = /(\b\d{4}-\d{2}-\d{2}\b|[A-Za-z0-9._%+'-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24})/g;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const ADDRESS = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export type EventPart = { text: string; kind: 'text' | 'day' | 'person' };

export function eventParts(body: string, eventKind: string): EventPart[] {
  if (!FORMATTED_KINDS.has(eventKind) || body.length > MAX_BODY) return [{ text: body, kind: 'text' }];
  return body
    .split(TOKEN)
    .filter((t) => t !== '')
    .map((text): EventPart => {
      if (DAY.test(text) && dayText(text) !== text) return { text: dayText(text), kind: 'day' };
      if (eventKind === 'reassigned' && ADDRESS.test(text)) return { text, kind: 'person' };
      return { text, kind: 'text' };
    });
}

export function EventText({ body, eventKind }: { body: string; eventKind: string }) {
  return (
    <>
      {eventParts(body, eventKind).map((p, i) => (
        <Fragment key={i}>{p.kind === 'person' ? <PersonName upn={p.text} /> : p.text}</Fragment>
      ))}
    </>
  );
}
